import React, { memo, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import numbro from 'numbro';

import { NumberFormatPolarity, SheetColumnType, TileDataSheetBackedWithSheet } from '@common/api';
import { useI18nContext } from '../../../i18n/i18n-react';
import { Button } from '../../../components/button';
import { ChevronDown, ChevronUp } from '@components/icons/FarseerIcons';

import {
    SingleValueComparisonData,
    SingleValueComparisonNumber,
    SingleValueData,
    SingleValueDataInput,
    SingleValueDataInputButtonContainer,
    SingleValueDataInputContainer,
} from './Tiles.style';

import { useSheetState } from '@features/sheet/editor/sheetState';
import { useFormatting } from '@providers/FormattingProvider';
import { useTileGlobalOverride } from './useTileGlobalOverride';
import { useTileConfigSync } from './useTileConfig';
import { GridLayoutIgnoreDrag } from '@components/grid-layout';
import { useSheetColumnConfig } from '@components/sheet/Sheet';
import { DashboardTileContent } from '../Dashboard.style';
import { useDerivedState } from '@hooks/useDerivedState';

const SINGLE_VALUE_SHEET_OPTIONS = {
    maxRows: 1,
    expandState: { '*': 0 },
    avoidFlash: false,
};

export const SingleValueTile = memo(
    ({
        data,
        canEdit,
        canDataEntry,
    }: {
        data: TileDataSheetBackedWithSheet;
        canEdit: boolean;
        canDataEntry: boolean;
    }) => {
        const { formatCell, formatNumber, parseNumber } = useFormatting();
        const globalOverride = useTileGlobalOverride(data);

        const localOverride = useMemo(
            () => ({
                ...globalOverride,
                ...SINGLE_VALUE_SHEET_OPTIONS,
            }),
            [globalOverride]
        );

        const sheetPath = `${data.sheet.id}`;
        const sheetContext = useSheetState(sheetPath, canDataEntry, canEdit, localOverride);
        const {
            state: {
                sheetConfig,
                sheetData: { rows, columns },
                canSaveForEveryone,
            },
            api: { resetConfig, saveForEveryone },
        } = sheetContext;

        useTileConfigSync(canSaveForEveryone, saveForEveryone, resetConfig);

        const {
            dataEntry = false,
            showDataEntryButtons = false,
            dataEntryButtonStep = '10%',
            comparisonCustomText = '',
        } = data.visualization.singleValue || {};

        const { LL } = useI18nContext();

        const { orderedColumns, columnLookup } = useSheetColumnConfig(columns, sheetConfig.display.columnConfig);
        const [colIdx, diffColIdx] = useMemo(() => {
            const arr: number[] = [];
            orderedColumns.forEach((col, i) => {
                if (col.type === SheetColumnType.Number) {
                    arr.push(columnLookup[i]);
                }
            });
            return [arr[0], arr[1]];
        }, [orderedColumns, columnLookup]);

        const cell = rows[0]?.cells[colIdx];
        const singleValue = cell?.value?.number;
        const diffCell = rows[0]?.cells[diffColIdx];
        const comparisonValue = diffCell?.value?.number;

        const formatOverride = data.visualization.singleValue?.format;
        const overrideCell = useMemo(
            () =>
                formatOverride && cell?.value
                    ? { ...cell.value, format: formatOverride.formatString, ...formatOverride }
                    : cell?.value,
            [formatOverride, cell]
        );

        const stringValue = formatCell(overrideCell);

        const trendFlip = overrideCell?.polarity === NumberFormatPolarity.Negative;

        const [resetValueTrigger, setResetValueTrigger] = useState<number>(0);
        const [singleValueAsString, setSingleValueAsString] = useDerivedState<string>(stringValue, [overrideCell]);

        const comparisonValueAsString = useMemo<string>(() => {
            let stringValue = '';
            if (
                singleValue !== null &&
                singleValue !== undefined &&
                comparisonValue !== null &&
                comparisonValue !== undefined &&
                !Number.isNaN(singleValue) &&
                !Number.isNaN(comparisonValue)
            ) {
                const diff = singleValue - comparisonValue;
                const diffPercent = (diff / comparisonValue) * 100;
                stringValue = `${diff >= 0 ? '+' : ''}${formatNumber(diff, overrideCell?.format)} (${numbro(
                    diffPercent
                ).format({
                    mantissa: 1,
                })}%)`;
            }
            return stringValue;
        }, [singleValue, comparisonValue, formatNumber, overrideCell?.format]);

        const dataEntryStep = useMemo<{ type: 'absolute' | 'relative' | 'error'; number: number }>(() => {
            const hasPercentSign = dataEntryButtonStep.trim().endsWith('%');
            const numberValue = parseNumber(dataEntryButtonStep.replace(/%$/g, ''));
            if (numberValue == null) {
                return { type: 'error', number: 0 };
            } else if (hasPercentSign) {
                return { type: 'relative', number: numberValue / 100 };
            } else {
                return { type: 'absolute', number: numberValue };
            }
        }, [dataEntryButtonStep, parseNumber]);

        function resetValue() {
            setResetValueTrigger(resetValueTrigger + 1);
        }

        async function saveNumberValue(numberValue: number) {
            sheetContext.api.updateCells([
                {
                    type: 'cell-update',
                    value: numberValue,
                    rowIndex: 0,
                    columnIndex: colIdx,
                },
            ]);
        }

        async function parseAndSaveValue() {
            const numberValue = parseNumber(singleValueAsString);
            if (numberValue == null) {
                toast.error(LL.ERROR_PARSING_INPUT_NUMBER());
            } else {
                try {
                    await saveNumberValue(numberValue);
                } catch {
                    toast.error(LL.ERROR_SAVING_DATA());
                }
            }
        }

        async function stepChangeValue(direction: 'up' | 'down') {
            if (!dataEntry) {
                toast.error(LL.DATA_ENTRY_NOT_ALLOWED());
                return;
            }
            if (dataEntryStep.type === 'error') {
                toast.error(LL.ERROR_PARSING_STEP_VALUE());
                return;
            }
            let newValue = singleValue ?? 0;
            if (dataEntryStep.type === 'absolute') {
                if (direction === 'up') {
                    newValue += dataEntryStep.number;
                } else if (direction === 'down') {
                    newValue -= dataEntryStep.number;
                }
            } else if (dataEntryStep.type === 'relative') {
                if (direction === 'up') {
                    newValue += dataEntryStep.number * newValue;
                } else if (direction === 'down') {
                    newValue -= dataEntryStep.number * newValue;
                }
            }
            if (newValue !== singleValue) {
                try {
                    await saveNumberValue(newValue);
                } catch {
                    toast.error(LL.ERROR_SAVING_DATA());
                }
            }
        }

        return (
            <DashboardTileContent>
                {dataEntry ? (
                    <GridLayoutIgnoreDrag>
                        <SingleValueDataInputContainer onMouseDown={(e) => e.stopPropagation()}>
                            <SingleValueDataInput
                                value={singleValueAsString}
                                onChange={(e) => setSingleValueAsString(e.target.value)}
                                onKeyDown={async (e) => {
                                    if (e.key === 'Enter') {
                                        await parseAndSaveValue();
                                    }
                                }}
                                onBlur={() => resetValue()}
                            />
                            {showDataEntryButtons && (
                                <SingleValueDataInputButtonContainer>
                                    <Button
                                        icon={<ChevronUp />}
                                        $type="quaternary"
                                        onClick={() => stepChangeValue('up')}
                                        style={{ height: 28 }}
                                    />
                                    <Button
                                        icon={<ChevronDown />}
                                        $type="quaternary"
                                        onClick={() => stepChangeValue('down')}
                                        style={{ height: 28 }}
                                    />
                                </SingleValueDataInputButtonContainer>
                            )}
                        </SingleValueDataInputContainer>
                    </GridLayoutIgnoreDrag>
                ) : (
                    <SingleValueData>
                        <GridLayoutIgnoreDrag>
                            <span>{singleValueAsString}</span>
                        </GridLayoutIgnoreDrag>
                    </SingleValueData>
                )}
                {singleValue !== null && comparisonValue !== null && comparisonValue !== undefined ? (
                    <SingleValueComparisonData>
                        <GridLayoutIgnoreDrag>
                            <span>
                                <SingleValueComparisonNumber
                                    $positive={trendFlip !== comparisonValueAsString.startsWith('+')}
                                >
                                    {comparisonValueAsString}
                                </SingleValueComparisonNumber>
                                <span>
                                    {comparisonCustomText !== ''
                                        ? ` ${comparisonCustomText}`
                                        : ` vs. ${columns[diffColIdx]?.name || ''}`}
                                </span>
                            </span>
                        </GridLayoutIgnoreDrag>
                    </SingleValueComparisonData>
                ) : null}
            </DashboardTileContent>
        );
    }
);
SingleValueTile.displayName = 'SingleValueTile';
